import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, GitBranch, Info, Loader2, Scale } from 'lucide-react';
import { apiClient } from '../../../api';

const STANCE_COLOR: Record<string, string> = { rule: '#D3D1C7', model: '#AFA9EC', human: '#5DCAA5' };

interface RuleSays { stance: string; gates: { rule_key: string; title: string; tier: string; rate_text: string; evidence: string }[] }
interface ModelSays {
  stance: string | null;
  probability: number | null;
  probability_source: 'out_of_fold' | 'live' | null;
  language: { decision: string; reason_code: string | null; citation: string | null; rationale: string | null } | null;
  internal_conflict: boolean;
}
interface HumanSays { stance: string | null; origin: 'operator' | 'source'; undecided: boolean }

interface LaneRow {
  eai_nbr: number;
  name: string;
  primary_data_center: string;
  app_type: string | null;
  rule_says: RuleSays | null;
  model_says: ModelSays | null;
  human_says: HumanSays | null;
  model_internal_conflict: boolean;
  conflict_pairs: number;
}

interface TrailRow {
  id: number;
  decision: string;
  provenance: string;
  reason_code: string | null;
  evidence_field: string | null;
  decided_by: string | null;
  decided_at: string;
  supersedes: number | null;
  note: string | null;
  is_current: boolean;
}

interface LaneData {
  scope: string;
  basis: string;
  basis_predicate: string;
  extract: { batch_id: number; as_of: string | null } | null;
  note: string;
  lane: { count: number; rows: LaneRow[] };
  source_conflicts: { count: number; rows: { eai_nbr: number; name: string; primary_data_center: string; disposition: string | null; label: string | null; resolved: boolean }[]; note: string };
  override_metric: {
    series: { month: string; model_calls: number; human_reviews: number; overrides: number; override_rate: number | null }[];
    totals: { model_calls: number; human_reviews: number; overrides: number; override_rate: number | null };
    note: string;
  };
}

const stanceLabel = (s: string | null | undefined) => (
  s === 'MIGRATES' ? 'Migrates' : s === 'DOES_NOT_MIGRATE' ? 'Does not migrate' : 'No position'
);

export default function DisagreementLane({ refreshKey, scope, basis }: {
  refreshKey: number;
  scope: string;
  basis: string;
}) {
  const [data, setData] = useState<LaneData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openEai, setOpenEai] = useState<number | null>(null);
  const [trails, setTrails] = useState<Record<number, TrailRow[] | 'loading'>>({});
  const [resolving, setResolving] = useState<number | null>(null);
  const [resolvedEais, setResolvedEais] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get('/api/cloud-portfolio/disagreements', { params: { scope, basis } })
      .then((r) => { if (!cancelled) { setData(r.data); setResolvedEais(new Set()); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load the disagreement lane'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, basis, refreshKey]);

  const loadTrail = (eai: number) => {
    if (trails[eai]) return;
    setTrails((t) => ({ ...t, [eai]: 'loading' }));
    apiClient.get(`/api/cloud-portfolio/decisions/${eai}/trail`)
      .then((r) => setTrails((t) => ({ ...t, [eai]: r.data.trail })))
      .catch(() => setTrails((t) => { const { [eai]: _, ...rest } = t; return rest; }));
  };

  const resolve = async (eai: number, decision: string) => {
    setResolving(eai);
    try {
      await apiClient.post('/api/cloud-portfolio/decisions', { eai_nbr: eai, decision, note: 'Resolved from the disagreement lane' });
      setResolvedEais((s) => new Set(s).add(eai));
      setOpenEai(null);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to record the decision');
    } finally {
      setResolving(null);
    }
  };

  if (loading) return <p className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading disagreement lane…</p>;
  if (!data) {
    return error ? (
      <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        {error}
      </div>
    ) : null;
  }

  const visibleRows = data.lane.rows.filter((r) => !resolvedEais.has(r.eai_nbr));

  const positionCell = (title: string, colorKey: string, body: JSX.Element | null) => (
    <div className="flex-1 min-w-[220px] bg-gray-50 border border-gray-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="w-2.5 h-2.5 rounded-sm border border-gray-300" style={{ background: STANCE_COLOR[colorKey] }} />
        <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">{title}</span>
      </div>
      {body || <p className="text-xs text-gray-400">No position.</p>}
    </div>
  );

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Scale className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-super-dark-purple">Disagreement lane · {data.scope}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-super-dark-purple text-white">{visibleRows.length}</span>
        </div>
        <p className="text-[11px] text-gray-400 mb-1">Basis {data.basis} ({data.basis_predicate}) · extract as of {data.extract?.as_of || '—'}</p>
        <p className="text-xs text-gray-500 mb-3">{data.note}</p>

        {visibleRows.length === 0 && (
          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
            No open disagreements in this scope — every application where rule, model and human differ has been ruled on.
          </p>
        )}

        <div className="divide-y">
          {visibleRows.map((row) => {
            const open = openEai === row.eai_nbr;
            const trail = trails[row.eai_nbr];
            return (
              <div key={row.eai_nbr} className="py-2">
                <button
                  onClick={() => setOpenEai(open ? null : row.eai_nbr)}
                  className="w-full flex items-center gap-2 text-left"
                >
                  {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                  <span className="text-xs font-semibold text-gray-700 truncate">{row.name}</span>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">EAI {row.eai_nbr} · {row.primary_data_center}</span>
                  <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                    {row.model_internal_conflict && (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px">LANGUAGE ↔ SCORER</span>
                    )}
                    <span className="text-[11px] text-gray-500">Rule {stanceLabel(row.rule_says?.stance)} · Model {stanceLabel(row.model_says?.stance)} · Human {stanceLabel(row.human_says?.stance)}</span>
                  </span>
                </button>

                {open && (
                  <div className="mt-2 ml-5 space-y-3">
                    <div className="flex gap-3 flex-wrap">
                      {positionCell('Rule says', 'rule', row.rule_says ? (
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-700">{stanceLabel(row.rule_says.stance)}</p>
                          {row.rule_says.gates.map((g) => (
                            <p key={g.rule_key} className="text-[11px] text-gray-500">
                              {g.title} ({g.tier}) — error {g.rate_text}<br />
                              <span className="text-gray-400">{g.evidence}</span>
                            </p>
                          ))}
                        </div>
                      ) : null)}
                      {positionCell('Model says', 'model', row.model_says ? (
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-700">
                            {stanceLabel(row.model_says.stance)}
                            {row.model_says.probability != null && (
                              <span className="font-normal text-gray-500"> · p = {row.model_says.probability}
                                {row.model_says.probability_source === 'out_of_fold' ? ' (out-of-fold)' : ''}
                              </span>
                            )}
                          </p>
                          {row.model_says.language && (
                            <p className="text-[11px] text-gray-500">
                              Language layer: {stanceLabel(row.model_says.language.decision)}
                              {row.model_says.language.reason_code ? ` · ${row.model_says.language.reason_code}` : ''}
                              {row.model_says.language.citation && <><br /><span className="text-gray-400">“{row.model_says.language.citation}”</span></>}
                            </p>
                          )}
                        </div>
                      ) : null)}
                      {positionCell('Human says', 'human', row.human_says ? (
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-700">
                            {row.human_says.undecided ? 'Marked undecided' : stanceLabel(row.human_says.stance)}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {row.human_says.origin === 'source' ? 'From the source assessment — Steve’s historical call, not yet ruled in-app.' : 'Ruled by an operator in this system.'}
                          </p>
                        </div>
                      ) : null)}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-gray-500">Resolve:</span>
                      <button
                        onClick={() => resolve(row.eai_nbr, 'MIGRATES')}
                        disabled={resolving === row.eai_nbr}
                        className="px-2.5 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                      >
                        Migrates
                      </button>
                      <button
                        onClick={() => resolve(row.eai_nbr, 'DOES_NOT_MIGRATE')}
                        disabled={resolving === row.eai_nbr}
                        className="px-2.5 py-1.5 rounded bg-gray-700 hover:bg-gray-800 text-white text-xs font-semibold disabled:opacity-50"
                      >
                        Does not migrate
                      </button>
                      <button
                        onClick={() => resolve(row.eai_nbr, 'UNDECIDED')}
                        disabled={resolving === row.eai_nbr}
                        className="px-2.5 py-1.5 rounded bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold disabled:opacity-50"
                      >
                        Needs interview
                      </button>
                      <button
                        onClick={() => loadTrail(row.eai_nbr)}
                        className="ml-auto flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700"
                      >
                        <GitBranch className="w-3 h-3" /> Audit trail
                      </button>
                    </div>

                    {trail === 'loading' && <p className="text-[11px] text-gray-400">Loading trail…</p>}
                    {Array.isArray(trail) && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-[11px] font-bold text-gray-600 mb-1.5">Decision trail — append-only, never overwritten</p>
                        {trail.length === 0 && <p className="text-[11px] text-gray-400">No decisions recorded for this application yet.</p>}
                        {trail.map((d) => (
                          <p key={d.id} className="text-[11px] text-gray-600">
                            {new Date(d.decided_at).toISOString().slice(0, 16).replace('T', ' ')} · <span className="font-semibold">{d.provenance}</span> · {stanceLabel(d.decision) || d.decision}
                            {d.reason_code ? ` · ${d.reason_code}` : ''}
                            {d.evidence_field ? ` · evidence: ${d.evidence_field}` : ''}
                            {d.decided_by ? ` · by ${d.decided_by}` : ''}
                            {d.supersedes != null ? ` · supersedes #${d.supersedes}` : ''}
                            {d.is_current ? ' · current' : ''}
                            {d.note ? <span className="text-gray-400"> — {d.note}</span> : null}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-super-dark-purple mb-1">Source scope conflicts · {data.source_conflicts.count}</p>
          <p className="text-xs text-gray-500 mb-3">{data.source_conflicts.note}</p>
          <div className="space-y-1.5">
            {data.source_conflicts.rows.map((c) => (
              <div key={c.eai_nbr} className="text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-700 truncate flex-1 min-w-0">{c.name}</span>
                  {c.resolved && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-px flex-shrink-0">ADJUDICATED</span>}
                </div>
                <p className="text-[11px] text-gray-400">EAI {c.eai_nbr} · {c.primary_data_center} · assessed “{c.disposition || '—'}” and on the out-of-scope tab</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-super-dark-purple mb-1">Human override rate of model calls</p>
          <p className="text-xs text-gray-500 mb-3">{data.override_metric.note}</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Model calls</p>
              <p className="text-xl font-bold text-super-dark-purple">{data.override_metric.totals.model_calls}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Human reviews</p>
              <p className="text-xl font-bold text-super-dark-purple">{data.override_metric.totals.human_reviews}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Override rate</p>
              <p className="text-xl font-bold text-super-dark-purple">{data.override_metric.totals.override_rate == null ? '—' : `${Math.round(data.override_metric.totals.override_rate * 100)}%`}</p>
            </div>
          </div>
          {data.override_metric.series.length > 0 && (
            <div className="mt-3 space-y-1">
              {data.override_metric.series.map((m) => (
                <p key={m.month} className="text-[11px] text-gray-500">
                  {m.month}: {m.overrides} of {m.human_reviews} reviews overrode the model ({m.model_calls} model calls)
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
